// required for the model viewer
import '@google/model-viewer';
import * as AR from '@dora/arctic';
import * as H from 'preact/hooks';
import { ModelViewerElement } from '@google/model-viewer';
import { $renderer } from '@google/model-viewer/lib/model-viewer-base';
import { Theme } from '@mui/material';
import { useDrawDimensionLines } from './dimensions';
import { createModelViewerElement, createModelViewerStyle } from './element';

export enum ModelViewerError {
    CONTEXT_LOST = 'CONTEXT_LOST',
    LOAD_FAILURE = 'LOAD_FAILURE',
    UNKNOWN = 'UNKNOWN',
}

interface ModelViewerResponse {
    // TODO: do data URLs need to be handled in some different way?
    /** trigger the model viewer, passing the model and a placement */
    activate: () => void;
    generateUSDZ: () => Promise<string>;
    setDimensionsEnabled: (enabled: boolean) => void;
    loading: boolean;
    progress: number | null;
    error: string | null;
    canActivateAr: boolean;
    isInitialLoad: boolean;
}

export const constructModelUrl = (baseUrl: string, sku: string, token: string) =>
    `${baseUrl}/${sku}.glb?token=${token}`;

/**
 * Construct a ModelViewer which, when activated, displays a model in AR.
 *
 * This function is only expected to be used a single time. If it is used more, then multiple model
 * viewers will be added into the DOM
 */
export const useModelViewer = (
    modelPreviewModeActive: boolean,
    dimensionsVisible: boolean,
    theme: Theme,
    src?: string,
    placement?: AR.Placement,
    targetElementId?: string,
    onSceneUserInteraction?: () => void,
): ModelViewerResponse => {
    const modelViewerElement = H.useRef<undefined | ModelViewerElement>();
    const [progress, setProgress] = H.useState<number>(0);
    const [error, setError] = H.useState<ModelViewerError | null>(null);
    const [canActivateAr, setCanActivateAr] = H.useState(false);
    const [isInitialLoad, setIsInitialLoad] = H.useState<boolean>(true);

    const { setDimensionsEnabled } = useDrawDimensionLines(modelViewerElement.current);

    H.useEffect(() => {
        modelViewerElement.current = createModelViewerElement(modelPreviewModeActive);
        const modelViewer = modelViewerElement.current;
        const modelViewerStyle = document.createElement('style');
        modelViewerStyle.textContent = createModelViewerStyle(theme.palette.primary.main);

        // We show the model viewer on desktop for Inspiration, this will attach it to the correct element (body if not provided)
        const target = document.getElementById(targetElementId ?? '') ?? document.body;
        target.appendChild(modelViewerStyle);
        target.appendChild(modelViewer);

        modelViewer.addEventListener('progress', (event: any) => {
            setProgress((event?.detail?.totalProgress as number) ?? 1);
            setIsInitialLoad(false);
        });

        modelViewer.addEventListener('error', (event: any) => {
            switch (event?.detail?.type) {
                case 'webglcontextlost':
                    setError(ModelViewerError.CONTEXT_LOST);
                    break;
                case 'loadfailure':
                    setError(ModelViewerError.LOAD_FAILURE);
                    break;
                default:
                    setError(ModelViewerError.UNKNOWN);
                    break;
            }
        });

        return () => {
            modelViewerElement.current?.remove();
            modelViewerElement.current = undefined;
        };
    }, []);

    H.useEffect(() => {
        modelViewerElement.current?.addEventListener('load', () => {
            handleLoad(modelViewerElement.current!);
        });
        return () => {
            modelViewerElement.current?.removeEventListener('load', () => handleLoad);
        };
    }, [dimensionsVisible, modelViewerElement]);

    const handleLoad = (modelViewer: ModelViewerElement) => {
        // set hotspot locations and values from bounding box
        const center = modelViewer.getBoundingBoxCenter();
        const size = modelViewer.getDimensions();

        // padding as a percentage (8%) of maximum dimension
        const max = Math.max(Math.max(size.x, size.y), size.z);
        const padding = max * 0.08;

        const x2 = size.x / 2;
        const y2 = size.y / 2;
        const z2 = size.z / 2;

        modelViewer.updateHotspot({
            name: 'hotspot-dot+X-Y+Z',
            position: `${center.x + x2} ${center.y - y2} ${center.z + z2}`,
        });

        modelViewer.updateHotspot({
            name: 'hotspot-dim+X-Y',
            position: `${center.x + x2 + padding} ${center.y - y2} ${center.z}`,
        });
        const dimXY1 = modelViewer.querySelector('button[slot="hotspot-dim+X-Y"]');
        if (dimXY1) dimXY1.textContent = `${(size.z * 100).toFixed(0)} cm`;

        modelViewer.updateHotspot({
            name: 'hotspot-dot+X-Y-Z',
            position: `${center.x + x2} ${center.y - y2} ${center.z - z2}`,
        });

        modelViewer.updateHotspot({
            name: 'hotspot-dim+X-Z',
            position: `${center.x + x2 + padding} ${center.y} ${center.z - z2}`,
        });
        const dimXZ1 = modelViewer.querySelector('button[slot="hotspot-dim+X-Z"]');
        if (dimXZ1) dimXZ1.textContent = `${(size.y * 100).toFixed(0)} cm`;

        modelViewer.updateHotspot({
            name: 'hotspot-dot+X+Y-Z',
            position: `${center.x + x2} ${center.y + y2} ${center.z - z2}`,
        });

        modelViewer.updateHotspot({
            name: 'hotspot-dim+Y-Z',
            position: `${center.x} ${center.y + y2 + padding} ${center.z - z2}`,
        });
        const dimYZ = modelViewer.querySelector('button[slot="hotspot-dim+Y-Z"]');
        if (dimYZ) dimYZ.textContent = `${(size.x * 100).toFixed(0)} cm`;

        modelViewer.updateHotspot({
            name: 'hotspot-dot-X+Y-Z',
            position: `${center.x - x2} ${center.y + y2} ${center.z - z2}`,
        });

        modelViewer.updateHotspot({
            name: 'hotspot-dim-X-Z',
            position: `${center.x - x2 - padding} ${center.y} ${center.z - z2}`,
        });
        const dimXZ2 = modelViewer.querySelector('button[slot="hotspot-dim-X-Z"]');
        if (dimXZ2) dimXZ2.textContent = `${(size.y * 100).toFixed(0)} cm`;

        modelViewer.updateHotspot({
            name: 'hotspot-dot-X-Y-Z',
            position: `${center.x - x2} ${center.y - y2} ${center.z - z2}`,
        });

        modelViewer.updateHotspot({
            name: 'hotspot-dim-X-Y',
            position: `${center.x - x2 - padding} ${center.y - y2} ${center.z}`,
        });
        const dimXY2 = modelViewer.querySelector('button[slot="hotspot-dim-X-Y"]');
        if (dimXY2) dimXY2.textContent = `${(size.z * 100).toFixed(0)} cm`;

        modelViewer.updateHotspot({
            name: 'hotspot-dot-X-Y+Z',
            position: `${center.x - x2} ${center.y - y2} ${center.z + z2}`,
        });

        // setup interaction callback
        let timer: any;
        let cameraLoaded = false;
        const debounceUserInteraction = () => {
            clearTimeout(timer);
            if (onSceneUserInteraction) {
                timer = setTimeout(onSceneUserInteraction, 1000);
            }
        };

        modelViewer.addEventListener('camera-change', (event: any) => {
            if (event?.detail?.source === 'user-interaction') {
                cameraLoaded && debounceUserInteraction();
                modelViewer.autoRotate = false;
                cameraLoaded = true;
            }
        });

        // Putting this behind a setTimout because the dimension lines have the tendency to go nuts
        setTimeout(() => setDimensionsEnabled(dimensionsVisible), 50);

        // loading is completed
        setProgress(1);
        setIsInitialLoad(false);
        setError(null);
        setCanActivateAr(modelViewer.canActivateAR);
    };

    H.useEffect(() => {
        if (modelViewerElement.current !== undefined && src !== undefined) {
            modelViewerElement.current.src = src;

            // reduce the initial zoom by increasing the FOV
            modelViewerElement.current.fieldOfView = '70deg';
            modelViewerElement.current.maxFieldOfView = '70deg';

            // on repeat views, ensure the canvas is the correct size
            modelViewerElement.current[$renderer]?.render(0);
        }
    }, [src]);

    H.useEffect(() => {
        setDimensionsEnabled(dimensionsVisible);
    }, [dimensionsVisible]);

    const setValues = (currSrc?: string, placement?: string) => {
        if (
            modelViewerElement.current !== undefined &&
            currSrc !== undefined &&
            placement !== undefined
        ) {
            try {
                modelViewerElement.current.src = currSrc;
                modelViewerElement.current.arPlacement = placement;
            } catch (e) {
                alert(e);
            }
        }
    };

    const activate = H.useCallback(() => {
        setValues(src, placement);
        modelViewerElement.current?.activateAR();
    }, [src, placement]);

    const generateUSDZ: () => Promise<string> = H.useCallback(() => {
        setValues(src, placement);
        // this is not part of the public API ...
        return (modelViewerElement.current as any).prepareUSDZ();
    }, [src, placement]);

    return {
        activate,
        generateUSDZ,
        setDimensionsEnabled,
        error,
        loading: progress !== 1,
        progress,
        canActivateAr,
        isInitialLoad,
    };
};
